import { Component, Input, OnInit, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DateHelpers, EdlEntriesFormBuilderService, EdlEntriesFormGroup, EDLGroupType, EdlPortalUserSearchCriteria, FileDownloadHandler, Group, PagedResponse, SessionService, User } from '@edl-development/edl-common';
import { StateService, Transition } from '@uirouter/core';
import { SelectGroupService } from 'src/app/groups/common/services/select-group.service';
import { InComplianceStatus } from 'src/app/item-packets/common/enums/in-compliance-status.enum';
import { ModalService } from 'src/app/modals/modal.service';
import { InComplianceReportEntry } from 'src/app/model/in-compliance-report-entry.model';
import { InComplianceReportRequest } from 'src/app/model/in-compliance-report-request.model';
import { EdlItemPacketEntryDTO } from 'src/app/model/item-packet/edl-item-packet-entry.model';
import { PagingHelpersService } from 'src/app/services/paging-helpers/paging-helpers.service';
import { VolunteerReportHelperService } from 'src/app/services/volunteer/volunteer-report-helper.service';
import { VolunteerReportService } from 'src/app/services/volunteer/volunteer-report.service';

@Component({
    templateUrl: './in-compliance-report.component.html',
    styleUrls: ['../../../reports.component.scss']
})
export class InComplianceReportComponent implements OnInit, OnChanges {
    @Input() searchCriteria: EdlPortalUserSearchCriteria;
    @Input() request: InComplianceReportRequest;
    @Input() user: User;
    @Input() pagedResponse: PagedResponse;
    @Input() currentPage: number;
    @Input() itemPackets: EdlItemPacketEntryDTO[] = []; // Assuming itemPackets is an array of objects

    isLoading: boolean = false;
    form: UntypedFormGroup;
    busy: boolean = false;
    reportContent: InComplianceReportEntry[] = [];
    isItemPacketSelected: boolean = false;
    DateHelpers = DateHelpers;

    EDLGroupType = EDLGroupType;

    constructor(
        public volunteerReportHelperService: VolunteerReportHelperService,
        private volunteerReportService: VolunteerReportService,
        private stateService: StateService,
        private sessionService: SessionService,
        private modalService: ModalService,
        private formBuilder: UntypedFormBuilder,
        public pagingHelperService: PagingHelpersService,
        private transition: Transition,
        private edlEntriesFormBuilderService: EdlEntriesFormBuilderService,
        private selectGroupService: SelectGroupService,
        private fileHandler: FileDownloadHandler,
    ) {
        this.currentPage = 1;
    }

    ngOnInit() {
        let params = this.transition.params();
        this.reportContent = this.pagedResponse?.Results;
        this.currentPage = params.currentPage || 1;

        let itemPacket = params.itemPacket;
        if (itemPacket == '' || itemPacket == undefined) {
            itemPacket = null;
        } else {
            this.isItemPacketSelected = true;
        }

        const entries = this.itemPackets
            .map((entry: EdlItemPacketEntryDTO) => ({
                id: entry.Id,
                name: entry.Title,
                details: entry
            }));
        const packetsForm = this.edlEntriesFormBuilderService.buildSelectedFormGroup(entries);

        const selectedVolunteerAreas = this.pagingHelperService.toArrayParam(params.volunteerAreas) ?? [];

        this.form = this.formBuilder.group({
            itemPacket: [itemPacket, []],
            volunteerAreas: this.selectGroupService.toGroupsSelectForm({
                selectedGroupIds: selectedVolunteerAreas,
                searchCriteria: this.searchCriteria,
                filterGroupType: EDLGroupType.VOLUNTEER_AREA
            }),
            searchBy: [this.request?.SearchBy, []]
        });

        packetsForm.enable()
        packetsForm.updateValueAndValidity();

        this.volunteerAreasForm.valueChanges.subscribe(value => {
            this.search(this.form);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        // When pagedResponse changes (new data arrived), turn off loading
        if (changes['pagedResponse'] && !changes['pagedResponse'].firstChange) {
            this.isLoading = false;
        }
    }

    get canViewItemPackets() {
        return this.itemPackets && this.itemPackets.length > 0;
    }

    get volunteerAreasForm(): EdlEntriesFormGroup<Group> {
        return this.form.get('volunteerAreas') as EdlEntriesFormGroup<Group>;
    }

    addItemPackets(itemTemplate?: TemplateRef<any> | null) {
        const edlEntries: any[] = this.itemPackets
            .map((entry: EdlItemPacketEntryDTO) => ({
                id: entry.Id,
                name: entry.Title,
                details: entry
            }));
        const selectedEntries = this.edlEntriesFormBuilderService.toSelectedEntries(this.form.get('itemPacket') as unknown as EdlEntriesFormGroup<EdlItemPacketEntryDTO>);
        this.form.get('itemPacket').patchValue(selectedEntries);
        this.form.get('itemPacket').updateValueAndValidity();
        this.modalService
            .edlEntriesSelectorFormModal({ edlEntries, selectedEntries, itemTemplate, columnCount: 1 })
            .then((result: any[]) => {
                this.edlEntriesFormBuilderService.updateSelectedFormGroup(this.form.get('itemPacket') as unknown as EdlEntriesFormGroup<EdlItemPacketEntryDTO>, result);
            }, () => { });
    }

    search({ value }: { value: { SearchBy: string } }) {
        let itemPacket = this.form.get('itemPacket').value;
        if (/*!this.busy && */itemPacket != null && itemPacket != '') {
            // this.busy = true;

            let volunteerAreasFormGroup = this.form.get('volunteerAreas') as EdlEntriesFormGroup<Group>;
            const volunteerAreas = Object.values(volunteerAreasFormGroup.value || {})
                .map((entry: Partial<{ id: string; name: string; details: Group }>) => entry.details?.GroupID)
                .filter((id): id is string => !!id);

            this.stateService.go('authenticated.reports.volunteer.in-compliance', {
                currentPage: 1,
                searchBy: this.form.get('searchBy').value,
                itemPacket: this.form.get('itemPacket').value,
                volunteerAreas: volunteerAreas
            }, { reload: true })
        }
    }

    public onPageChange(page: number) {
        const currentStatePage = Number(this.transition.params().currentPage);

        // Only show loading if we're actually navigating to a different page
        if (page !== currentStatePage) {
            this.isLoading = true;
        }

        this.stateService
            .go(this.stateService.current.name, {
                currentPage: page
            });
    }


    filterGroups(groupsForm: EdlEntriesFormGroup<Group>, filterGroupType: EDLGroupType) {
        let selectedGroups = Object.values(groupsForm.value || {})

        this.selectGroupService.openFilterGroupsModal({
            groupsForm,
            groups: this.searchCriteria.PortalGroups,
            filterGroupType,
            keepFilteredOutGroups: true
        });
        // .finally(() => {
        //     let newSelectedGroups = Object.values(groupsForm.value || {});
        //     // if (JSON.stringify(selectedGroups) !== JSON.stringify(newSelectedGroups)) {
        //     //     this.search(this.form);
        //     // }
        // });
    }

    getItemPacket(itemPacketId?: string): EdlItemPacketEntryDTO | undefined {
        return this.itemPackets.find((packet: EdlItemPacketEntryDTO) => packet.Id === itemPacketId) || undefined;
    }

    getInComplianceStatus(isInCompliance: boolean) {
        return isInCompliance ? InComplianceStatus.IN_COMPLIANCE : InComplianceStatus.OUT_OF_COMPLIANCE;
    }

    downloadReport() {
        this.busy = true;

        var downloadRequest = {
            ...this.request,
            GetAllResults: true
        }

        this.volunteerReportService
            .downloadInComplianceReport(downloadRequest)
            .subscribe((fileResponse: Blob) => {
                console.log("File response received for download:", fileResponse);
                this.fileHandler.saveFile(fileResponse, "VolunteerInComplianceReport.csv");
                this.busy = false;
            },
                (error) => {
                    this.busy = false;
                });
    }
}
